import { z } from 'zod';

import { Expression } from '../../Expressions/AbstractExpressionEvaluator';
import { PerCallFunctionContext } from '../../Expressions/FunctionContext';
import { WorkflowExpressionEvaluator as ExpressionEvaluator } from '../../Expressions/WorkflowExpressionEvaluator';

export const RESERVED_CLOSURE_REASON_IDS = {
  automaticallyCompleted: 'automaticallyCompleted',
  automaticallyCancelled: 'automaticallyCancelled',
  manuallyCompleted: 'manuallyCompleted',
  manuallyCancelled: 'manuallyCancelled',
} as const;

export const RESERVED_CLOSURE_REASON_DESCRIPTIONS: Record<
  Exclude<keyof typeof RESERVED_CLOSURE_REASON_IDS, 'manuallyCompleted' | 'manuallyCancelled'>,
  string
> = {
  automaticallyCompleted: 'Completed automatically',
  automaticallyCancelled: 'Cancelled automatically',
};

const legacyAutomaticResolutionDefinition = Expression('boolean').meta({
  description:
    'An expression that evaluates to a boolean indicating whether the workflow definition can be automatically completed.',
  example: ExpressionEvaluator.boolean`false`,
});

const automaticResolutionDefinition = z.object({
  expression: Expression('boolean').meta({
    description:
      'An expression that evaluates to a boolean indicating whether the workflow definition can be automatically completed.',
    example: ExpressionEvaluator.boolean`false`,
  }),
  reason: z
    .string()
    .meta({
      description: `The reason for the automatic resolution. Defaults to ${RESERVED_CLOSURE_REASON_IDS.automaticallyCompleted} if not provided.`,
      example: 'Relief granted',
    })
    .optional(),
  description: z
    .string()
    .meta({
      description: `The description of the automatic resolution. Defaults to ${RESERVED_CLOSURE_REASON_DESCRIPTIONS.automaticallyCompleted} if not provided.`,
      example: 'Relief granted',
    })
    .optional(),
});

export const AutomaticResolutionDefinition = z.union([
  legacyAutomaticResolutionDefinition.transform((expr) => ({
    expression: expr,
  })),
  automaticResolutionDefinition,
  z.array(automaticResolutionDefinition).meta({
    description:
      'An array of automatic resolution expressions - the expressions are evaluated in order and the first one that evaluates to true is used.',
  }),
]);
export type AutomaticResolutionDefinition = z.infer<typeof AutomaticResolutionDefinition>;

const ResolutionReason = z.object({
  reason: z.string(),
  description: z.string(),
});
export type ResolutionReason = z.infer<typeof ResolutionReason>;

export function resolveAutomaticResolutionExpression(
  expression: AutomaticResolutionDefinition,
  evaluator: ExpressionEvaluator,
  ctx: PerCallFunctionContext,
): ResolutionReason | undefined {
  const expressions = Array.isArray(expression) ? expression : [expression];
  for (const expr of expressions) {
    const result = resolveSingleExpression(expr, evaluator, ctx);
    if (result != null) {
      return result;
    }
  }
  return undefined;
}

function resolveSingleExpression(
  expression: Exclude<AutomaticResolutionDefinition, Array<z.infer<typeof automaticResolutionDefinition>>>,
  evaluator: ExpressionEvaluator,
  ctx: PerCallFunctionContext,
): ResolutionReason | undefined {
  const result = evaluator.evaluate(expression.expression, ctx);
  if (!result) {
    return undefined;
  }
  const reason =
    'reason' in expression && expression.reason != null
      ? expression.reason
      : RESERVED_CLOSURE_REASON_IDS.automaticallyCompleted;
  const description =
    'description' in expression && expression.description != null
      ? expression.description
      : RESERVED_CLOSURE_REASON_DESCRIPTIONS.automaticallyCompleted;
  return {
    reason,
    description,
  };
}
